import Timecode from 'smpte-timecode';
import { DROPFRAME_DELIMITER, DROPFRAME_FRAMERATES } from 'src/constants';
import { Timecode as SipTimecode, toFramesWithFallback } from 'src/utils/timecode';

export function formatPoint(val: string): string {
  return (val.split(':')[0].length > 1 ? val : `0${val}`).replace("'", ':');
}

export function getDiffTimecode(from: string, to: string, framerate: number, df: boolean): string {
  const f = Timecode(from, framerate, df);
  const t = Timecode(to, framerate, df);
  t.subtract(f);
  return t.toString();
}

export function convertTimeCodeToSeconds(timeString: string, framerate: number = 23.976): number {
  const timeArray = timeString.replace(';', ':').split(':');
  const hours = parseInt(timeArray[0], 10) * 60 * 60;
  const minutes = parseInt(timeArray[1], 10) * 60;
  const seconds = parseInt(timeArray[2], 10);
  let drift = 0;
  if (framerate === 23.976 || framerate === 23.98 || framerate === 29.97) {
    const totalSeconds = hours + minutes + seconds;
    const allMinutesDividedByTen = ((hours + minutes + seconds) / 60 / 10) * (Number(framerate.toFixed(2)) / 10);
    drift = totalSeconds * (Number(framerate.toFixed(2)) / 1000) - allMinutesDividedByTen;
  }

  const frames = (parseInt(timeArray[3], 10) + drift) / framerate;
  return hours + minutes + seconds + frames;
}

interface ConvertSecondsToTimeCodeParams {
  secs: number;
  frameRate: number;
  inpoint: string;
  df: boolean;
}

export const convertSecondsToTimeCode = ({ secs, frameRate, inpoint, df }: ConvertSecondsToTimeCodeParams): string => {
  const v = Number(frameRate);
  const isNtscFamily = Math.abs(v - 29.97) < 1e-6 || Math.abs(v - 59.94) < 1e-6 || Math.abs(v - 119.88) < 1e-6;
  const dropFrame = typeof df === 'boolean' ? df : isNtscFamily;

  const tc = new SipTimecode(v, dropFrame);
  const startFrames = toFramesWithFallback(inpoint, v, dropFrame);
  const addFrames = tc.secondsToFrames(secs);
  return tc.fromFrames(startFrames + addFrames);
};

export const frameRateIsDropFrame = (frameRate: number | string): boolean => {
  return (DROPFRAME_FRAMERATES as readonly (string | number)[]).includes(frameRate);
};

export const timecodeIsDropFrame = (timecode: string): boolean => {
  return timecode.includes(DROPFRAME_DELIMITER);
};

export const cleanFrameRate = (fr: string): string => {
  const re = /[0-9.]+/g;
  const matches = fr.match(re);
  if (matches) {
    return matches.join('');
  }
  return '';
};

export const standardizeTimeCode = (tc: string): string => {
  const re = /[^0-9:]+/g;
  tc = tc.replace(re, ':');
  const [hours, minutes, seconds, frames] = tc.split(':');
  const newTc = new Timecode({
    hours: Number(hours),
    minutes: Number(minutes),
    seconds: Number(seconds),
    frames: Number(frames),
    dropFrame: false,
  });
  return newTc.toString();
};

export const replaceLastDelimiter = (timecode: string, newDelimiter: string): string => {
  const regex = /[:;._-](?=[^:;._-]*$)/;
  const lastDelimiterIndex = timecode.search(regex);

  if (lastDelimiterIndex >= 0) {
    const newTimecode = timecode.slice(0, lastDelimiterIndex) + newDelimiter + timecode.slice(lastDelimiterIndex + 1);
    return newTimecode;
  } else {
    return timecode;
  }
};

interface CreateTimecodeStringParams {
  seconds: number;
  frameRate: number;
  df: boolean;
  inpoint: string;
  finalDelimiter: string;
}

export const createTimecodeString = ({
  seconds,
  frameRate,
  df,
  inpoint,
  finalDelimiter,
}: CreateTimecodeStringParams): string => {
  const tc = convertSecondsToTimeCode({ secs: seconds, frameRate, inpoint, df });
  return replaceLastDelimiter(tc, finalDelimiter);
};
